import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

const SITE_URL = "https://letslistbusiness.com";

const SECTIONS = [
  { id: "collect", title: "Information we collect", body: "We collect account information you provide (name, email), content you submit (reviews, listings), and basic usage analytics like pages visited and approximate location." },
  { id: "use", title: "How we use information", body: "We use information to operate the service, surface relevant listings, prevent abuse, and communicate with you about your account or the service." },
  { id: "sharing", title: "Sharing", body: "We never sell your personal information. We may share aggregated, non-identifying data with partners. Service providers process data on our behalf under strict agreements." },
  { id: "rights", title: "Your rights", body: "You may request access, correction, or deletion of your personal data at any time by contacting us." },
  { id: "contact", title: "Contact", body: "Questions? Email privacy@letslistbusiness.com." },
];

export const Route = createFileRoute("/privacy-policy/")({
  head: () => {
    const title = "Privacy Policy | LetsListBusiness";
    const description = "Learn how LetsListBusiness handles and protects your personal data through our comprehensive privacy policy.";
    const url = SITE_URL + "/privacy-policy/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Privacy Policy" },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Privacy Policy", href: "/privacy-policy/" },
        ], SITE_URL),
      ],
    };
  },
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Privacy Policy" }]} />

      <section className="container mx-auto px-4 pb-16">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* TOC sidebar */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">On this page</div>
              <nav className="mt-4 space-y-1 border-l border-border">
                {SECTIONS.map((s, i) => (
                  <a key={s.id} href={`#${s.id}`} className="block border-l-2 border-transparent py-1.5 pl-4 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                    <span className="mr-2 font-mono text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9">
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Legal</div>
            <h1 className="mt-3 font-display text-5xl font-bold">Privacy Policy</h1>
            <p className="mt-3 text-sm text-muted-foreground">Last updated: January 2025</p>

            <p className="mt-8 text-lg leading-relaxed text-foreground/85">
              This Privacy Policy describes how LetsListBusiness ("we", "us") collects,
              uses, and shares information when you use our website. By using
              LetsListBusiness, you agree to the practices described here.
            </p>

            <div className="mt-12 space-y-12">
              {SECTIONS.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-sm text-primary">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="font-display text-2xl font-bold">{s.title}</h2>
                  </div>
                  <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                </section>
              ))}
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
